import React, { useCallback, useEffect, useState, useContext } from 'react';
import {
  Button,
  Container,
  Paper,
  Stack,
  Typography,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Breadcrumbs,
  TextField,
  IconButton,
  Box,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Checkbox,
  Chip,
  Tooltip,
} from '@mui/material';
import '../../src/styles/global.css';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import EmailIcon from '@mui/icons-material/Email';
import { toast } from '../utils/toast';
import { request } from '../utils/request';
import { ColorModeContext } from '../index';
import ReusableTable from '../components/Table/ReusableTable';
import ReusableHeader from '../components/Header/ReusableHeader';
import TableFilterBar from '../components/Table/TableFilterBar';
import { hasFeatureAccess, getStoredUser, FEATURES } from '../utils/rbac';
import AccessDenied from '../components/guards/AccessDenied';

/* =========================================================
   mEDGE design tokens (mirrors global.css :root values).
   DARK_VARS re-maps the same variable names for dark mode so
   every element styled via global.css classes (which all read
   var(--x)) repaints automatically — no separate dark stylesheet
   needed, and it works inside MUI portals (Dialog/Menu/Snackbar)
   because we re-apply these vars on each portal's root node.
   ========================================================= */

const LIGHT_VARS = {
  '--vault-navy': '#0B2545',
  '--edge-blue': '#1F6FEB',
  '--canvas': '#FFFFFF',
  '--mist': '#F7F9FC',
  '--hairline': '#E4E9F0',
  '--blue-tint': '#EAF2FE',
  '--slate': '#6B7A90',
  '--secure': '#00A88F',
  '--high': '#E2622B',
  '--elevated': '#F0A202',
  '--critical': '#D6323A',
  '--border-radius': '6px',
};

const DARK_VARS = {
  '--vault-navy': '#F4F7FB',
  '--edge-blue': '#4C9AFF',
  '--canvas': '#0F1729',
  '--mist': '#252b3b',
  '--hairline': 'rgba(255,255,255,0.14)',
  '--blue-tint': 'rgba(76,154,255,0.16)',
  '--slate': '#93A4BD',
  '--secure': '#2FD3B8',
  '--high': '#F07C46',
  '--elevated': '#FFC24B',
  '--critical': '#FF6B72',
  '--border-radius': '6px',
};

export const UserManagement = () => {
  const user = getStoredUser();
  const canRead = hasFeatureAccess(user, FEATURES.USER_MANAGEMENT, 'read');
  const canCreate = hasFeatureAccess(user, FEATURES.USER_MANAGEMENT, 'create');
  const canUpdate = hasFeatureAccess(user, FEATURES.USER_MANAGEMENT, 'update');
  const canDelete = hasFeatureAccess(user, FEATURES.USER_MANAGEMENT, 'delete');

  const { mode } = useContext(ColorModeContext || { mode: 'light' });
  const V = mode === 'dark' ? DARK_VARS : LIGHT_VARS;

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({});
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [enrolledUserIds, setEnrolledUserIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialMfa = () => {
    const m = searchParams.get('mfa');
    if (m === 'disabled' || m === 'false' || m === 'none' || m === 'unregistered') return 'Not Enrolled';
    if (m === 'enabled' || m === 'true' || m === 'enrolled') return 'Enrolled';
    return '';
  };

  const [mfaFilter, setMfaFilter] = useState(getInitialMfa);

  useEffect(() => {
    const m = searchParams.get('mfa');
    const resolved =
      m === 'disabled' || m === 'false' || m === 'none' || m === 'unregistered'
        ? 'Not Enrolled'
        : m === 'enabled' || m === 'true' || m === 'enrolled'
          ? 'Enrolled'
          : '';
    if (m !== null) {
      setMfaFilter(resolved);
    }
  }, [searchParams]);

  const getInitialSource = () => {
    const s = searchParams.get('source');
    if (!s) return '';
    const lower = s.toLowerCase();
    if (lower === 'ad' || lower === 'active directory') return 'Active Directory';
    if (lower === 'scim') return 'SCIM';
    if (lower === 'manual') return 'Manual';
    return '';
  };

  const [sourceFilter, setSourceFilter] = useState(getInitialSource);

  useEffect(() => {
    const s = searchParams.get('source');
    if (s !== null) {
      const lower = s.toLowerCase();
      if (lower === 'ad' || lower === 'active directory') setSourceFilter('Active Directory');
      else if (lower === 'scim') setSourceFilter('SCIM');
      else if (lower === 'manual') setSourceFilter('Manual');
      else setSourceFilter('');
    }
  }, [searchParams]);

  const isUserLocked = (u) => {
    if (!u) return false;
    if (u.isLocked !== true) return false;
    if (u.lockoutUntil) {
      return new Date(u.lockoutUntil) > new Date();
    }
    return true;
  };

  const formatDateTime = (val, fallback = 'N/A') => {
    if (!val) return fallback;
    const d = new Date(val);
    return isNaN(d.getTime()) ? String(val) : d.toLocaleString();
  };

  const getLockTooltip = (u) => {
    const lockedAtStr = formatDateTime(u.lockedAt, 'N/A');
    const lockoutUntilStr = u.lockoutUntil
      ? formatDateTime(u.lockoutUntil)
      : 'Until an administrator unlocks';
    return `Locked at: ${lockedAtStr} | Locked until: ${lockoutUntilStr}`;
  };

  const getInitialLocked = () => {
    const l = searchParams.get('locked');
    if (l === 'true' || l === 'locked' || l === '1') return 'Locked';
    if (l === 'false' || l === 'unlocked' || l === '0') return 'Unlocked';
    return '';
  };

  const [lockedFilter, setLockedFilter] = useState(getInitialLocked);

  useEffect(() => {
    const l = searchParams.get('locked');
    if (l !== null) {
      if (l === 'true' || l === 'locked' || l === '1') setLockedFilter('Locked');
      else if (l === 'false' || l === 'unlocked' || l === '0') setLockedFilter('Unlocked');
      else setLockedFilter('');
    }
  }, [searchParams]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Unlock User state
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockingUser, setUnlockingUser] = useState(null);
  const [unlocking, setUnlocking] = useState(false);

  const handleUnlockClick = (u) => {
    setUnlockingUser(u);
    setUnlockOpen(true);
  };

  const handleCancelUnlock = () => {
    if (unlocking) return;
    setUnlockingUser(null);
    setUnlockOpen(false);
  };

  const handleConfirmUnlock = async () => {
    if (!unlockingUser || !unlockingUser._id) return;
    setUnlocking(true);
    try {
      await request(`user/${unlockingUser._id}/unlock`, { method: 'post' });
      toast('User unlocked', { variant: 'success' });
      showSnack(`User "${unlockingUser.username || unlockingUser.email}" unlocked successfully.`, 'success');
      setUsers(prev => prev.map(u => (u._id === unlockingUser._id ? { ...u, isLocked: false, failedLoginAttempts: 0, lockoutUntil: null } : u)));
      await fetchUsers();
      handleCancelUnlock();
    } catch (err) {
      const msg = err?.message || 'Failed to unlock user';
      toast(msg, { variant: 'error' });
      showSnack(msg, 'error');
      setUnlocking(false);
    }
  };

  // Login Security Settings Dialog state
  const [securityOpen, setSecurityOpen] = useState(false);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securitySaving, setSecuritySaving] = useState(false);
  const [securityServerError, setSecurityServerError] = useState('');
  const [initialPolicy, setInitialPolicy] = useState({
    maxFailedAttempts: 5,
    lockDurationMinutes: 0,
    observationWindowMinutes: 30,
  });
  const [policyForm, setPolicyForm] = useState({
    maxFailedAttempts: 5,
    lockDurationMinutes: 0,
    observationWindowMinutes: 30,
  });
  const [policyErrors, setPolicyErrors] = useState({});

  const fetchSecurityPolicy = useCallback(async () => {
    setSecurityLoading(true);
    setSecurityServerError('');
    try {
      const res = await request('security-settings/login', { method: 'get' });
      if (res && res.policy) {
        const p = {
          maxFailedAttempts: res.policy.maxFailedAttempts ?? 5,
          lockDurationMinutes: res.policy.lockDurationMinutes ?? 0,
          observationWindowMinutes: res.policy.observationWindowMinutes ?? 30,
        };
        setInitialPolicy(p);
        setPolicyForm(p);
        setPolicyErrors({});
      }
    } catch (err) {
      setSecurityServerError(err?.message || 'Failed to load login security policy');
    } finally {
      setSecurityLoading(false);
    }
  }, []);

  const openSecurityDialog = () => {
    setSecurityOpen(true);
    fetchSecurityPolicy();
  };

  const handlePolicyChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...policyForm, [name]: value };
    setPolicyForm(updated);

    const errors = {};
    const maxVal = value !== '' && name === 'maxFailedAttempts' ? Number(value) : Number(updated.maxFailedAttempts);
    if (
      (name === 'maxFailedAttempts' ? value === '' : updated.maxFailedAttempts === '') ||
      isNaN(maxVal) ||
      !Number.isInteger(maxVal) ||
      maxVal < 3 ||
      maxVal > 10
    ) {
      errors.maxFailedAttempts = 'Must be an integer between 3 and 10';
    }

    const lockVal = value !== '' && name === 'lockDurationMinutes' ? Number(value) : Number(updated.lockDurationMinutes);
    if (
      (name === 'lockDurationMinutes' ? value === '' : updated.lockDurationMinutes === '') ||
      isNaN(lockVal) ||
      !Number.isInteger(lockVal) ||
      (lockVal !== 0 && (lockVal < 5 || lockVal > 1440))
    ) {
      errors.lockDurationMinutes = 'Must be 0 (until administrator unlocks) or between 5 and 1440 minutes';
    }

    const winVal = value !== '' && name === 'observationWindowMinutes' ? Number(value) : Number(updated.observationWindowMinutes);
    if (
      (name === 'observationWindowMinutes' ? value === '' : updated.observationWindowMinutes === '') ||
      isNaN(winVal) ||
      !Number.isInteger(winVal) ||
      winVal < 5 ||
      winVal > 1440
    ) {
      errors.observationWindowMinutes = 'Must be an integer between 5 and 1440 minutes';
    }

    setPolicyErrors(errors);
  };

  const isPolicyUnchanged =
    Number(policyForm.maxFailedAttempts) === Number(initialPolicy.maxFailedAttempts) &&
    Number(policyForm.lockDurationMinutes) === Number(initialPolicy.lockDurationMinutes) &&
    Number(policyForm.observationWindowMinutes) === Number(initialPolicy.observationWindowMinutes);

  const isSaveDisabled =
    securitySaving ||
    securityLoading ||
    Object.keys(policyErrors).length > 0 ||
    isPolicyUnchanged ||
    !canUpdate;

  const handleSaveSecurityPolicy = async (e) => {
    if (e) e.preventDefault();
    if (isSaveDisabled) return;
    setSecuritySaving(true);
    setSecurityServerError('');
    try {
      const res = await request('security-settings/login', {
        method: 'put',
        body: {
          maxFailedAttempts: Number(policyForm.maxFailedAttempts),
          lockDurationMinutes: Number(policyForm.lockDurationMinutes),
          observationWindowMinutes: Number(policyForm.observationWindowMinutes),
        },
      });
      if (res && res.policy) {
        const p = {
          maxFailedAttempts: res.policy.maxFailedAttempts,
          lockDurationMinutes: res.policy.lockDurationMinutes,
          observationWindowMinutes: res.policy.observationWindowMinutes,
        };
        setInitialPolicy(p);
        setPolicyForm(p);
      }
      showSnack('Login security policy updated successfully', 'success');
      setSecurityOpen(false);
    } catch (err) {
      const msg = err?.message || 'Failed to update login security policy';
      setSecurityServerError(msg);
      showSnack(msg, 'error');
    } finally {
      setSecuritySaving(false);
    }
  };

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  // Removed labelCutoutSx as we are using outside labels now.

  const selectMenuProps = {
    PaperProps: {
      className: 'global-card',
      style: V,
      sx: {
        mt: 0.5,
        '& .MuiList-root': { padding: 0 },
        '& .MuiMenuItem-root': {
          fontFamily: 'Calibri, Arial, sans-serif',
          fontSize: 17,
          color: 'var(--vault-navy)',
        },
        '& .MuiMenuItem-root[data-value=""]': {
          backgroundColor: 'var(--mist)',
          borderBottom: '1px solid var(--hairline)',
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: 'var(--mist)',
        },
        '& .Mui-selected': {
          backgroundColor: 'var(--blue-tint) !important',
        },
      },
    },
  };

  const fetchRoles = useCallback(async () => {
    try {
      const data = await request('role/dropdown', { method: 'get' });
      setRoles(data.data || []);
    } catch (err) {
      showSnack('Failed to fetch roles', 'error');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const [userData, deviceResponse] = await Promise.all([
        request('user', { method: 'get' }),
        request('devices', { method: 'get', query: { limit: 1000, skip: 0 } }).catch(() => null),
      ]);

      const rawUsers = Array.isArray(userData)
        ? userData
        : Array.isArray(userData?.data)
          ? userData.data
          : [];
      setUsers(rawUsers);

      const devList = Array.isArray(deviceResponse?.data?.docs)
        ? deviceResponse.data.docs
        : Array.isArray(deviceResponse?.docs)
          ? deviceResponse.docs
          : Array.isArray(deviceResponse?.data)
            ? deviceResponse.data
            : Array.isArray(deviceResponse)
              ? deviceResponse
              : [];

      // Build user lookup maps to correlate devices where userId is an ObjectId, email, or username
      const usersById = new Map();
      const usersByEmail = new Map();
      const usersByUsername = new Map();

      rawUsers.forEach((u) => {
        if (!u) return;
        if (u._id) usersById.set(String(u._id), u);
        if (u.email) usersByEmail.set(String(u.email).trim().toLowerCase(), u);
        if (u.username) usersByUsername.set(String(u.username).trim().toLowerCase(), u);
      });

      const activeEnrolledIds = new Set();
      devList.forEach((d) => {
        if (d && d.isBlocked !== true && d.revokedAt == null) {
          const rawId = d.userId ? String(d.userId).trim() : '';
          const matchedUser =
            usersById.get(rawId) ||
            usersByEmail.get(rawId.toLowerCase()) ||
            usersByUsername.get(rawId.toLowerCase());

          // If device matches a user record, register all that user's identifiers
          if (matchedUser) {
            [matchedUser._id, matchedUser.email, matchedUser.username, matchedUser.emp_id].forEach((val) => {
              if (val) {
                const str = String(val).trim();
                activeEnrolledIds.add(str);
                activeEnrolledIds.add(str.toLowerCase());
              }
            });
          }

          // Also register any identifiers found directly on the device record
          [d.userId, d.userEmail, d.email, d.userName, d.username].forEach((val) => {
            if (val) {
              const str = String(val).trim();
              activeEnrolledIds.add(str);
              activeEnrolledIds.add(str.toLowerCase());
            }
          });
        }
      });
      setEnrolledUserIds(activeEnrolledIds);
    } catch (err) {
      showSnack('Failed to fetch users', 'error');
    }
  }, []);

  useEffect(() => {
    if (!canRead) return;
    fetchRoles();
    fetchUsers();
  }, [canRead, fetchRoles, fetchUsers]);

  const openDialog = (user = null) => {
    if (user) {
      setFormData({
        ...user,
        password: '',
      });
      setSelectedId(user._id);
      setIsEdit(true);
    } else {
      setFormData({ status: 'active', password: '' });
      setSelectedId(null);
      setIsEdit(false);
    }
    setOpen(true);
  };

  const closeDialog = () => setOpen(false);

  const handleChange = e =>
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setFormData(prev => ({ ...prev, profile_image_file: file }));
  };

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.first_name || !formData.last_name || !formData.username || !formData.email) {
      toast('First Name, Last Name, Username, and Email are required.', { variant: 'error' });
      showSnack('First Name, Last Name, Username, and Email are required.', 'error');
      return;
    }
    if (!isEdit && !formData.password) {
      toast('Password is required.', { variant: 'error' });
      showSnack('Password is required.', 'error');
      return;
    }

    setLoading(true);
    try {
      const fd = new FormData();

      const fields = [
        'first_name', 'last_name', 'username', 'email', 'department',
        'designation', 'roleId', 'mobile', 'emp_id', 'status'
      ];
      fields.forEach(f => {
        if (typeof formData[f] !== 'undefined') {
          fd.append(f, formData[f]);
        }
      });

      if (!isEdit) {
        if (formData.password) {
          fd.append('password', formData.password);
        }
      } else {
        const isAdUser = isEdit && (formData.authMethod === 'ad' || formData.source === 'ad');
        if (!isAdUser && formData.password && typeof formData.password === 'string' && formData.password.trim() !== '') {
          fd.append('password', formData.password.trim());
        }
      }

      if (formData.profile_image_file) {
        fd.append('profile_image', formData.profile_image_file);
      }

      if (isEdit) {
        await request('user', {
          method: 'put',
          params: selectedId,
          body: fd
        });
        toast('User updated', { variant: 'success' });
        showSnack('User updated successfully', 'success');
      } else {
        await request('register', {
          method: 'post',
          body: fd
        });
        toast('User created', { variant: 'success' });
        showSnack('User created successfully', 'success');
      }

      await fetchUsers();
      closeDialog();
    } catch (err) {
      let errorMessage = 'Error saving user';
      if (err?.errors && typeof err.errors === 'object') {
        errorMessage = Object.values(err.errors).join(', ');
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      toast(errorMessage, { variant: 'error' });
      showSnack(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setDeletingUser(user);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingUser(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser || !deletingUser._id) return;
    setDeleting(true);
    try {
      await request('user', { method: 'delete', params: deletingUser._id });
      toast('User deleted', { variant: 'success' });
      showSnack(`User "${deletingUser.username || deletingUser.email}" deleted.`, 'success');
      await fetchUsers();
      handleCancelDelete();
    } catch (err) {
      toast('Error deleting user', { variant: 'error' });
      showSnack('Failed to delete user', 'error');
      setDeleting(false);
    }
  };

  const sorted = [...users].sort((a, b) =>
    (a.first_name || '').localeCompare(b.first_name || '')
  );

  const isUserMfaEnrolled = useCallback((u) => {
    if (!u) return false;
    // 1. Direct boolean flags on user record
    if (
      u.mfaEnabled === true ||
      u.isTotpEnabled === true ||
      u.isMfaEnrolled === true ||
      u.mfa_enrolled === true
    ) {
      return true;
    }

    // 2. Secret presence (TOTP)
    if (u.totpSecret && u.totpSecret !== 'null' && String(u.totpSecret).trim() !== '') {
      return true;
    }

    // 3. Embedded method/device/passkey arrays if returned by backend
    if (Array.isArray(u.mfaMethods) && u.mfaMethods.length > 0) return true;
    if (Array.isArray(u.devices) && u.devices.length > 0) return true;
    if (Array.isArray(u.passkeys) && u.passkeys.length > 0) return true;

    // 4. Match against active devices registered by user ID, email, username, or emp_id
    const userIdentifiers = [u._id, u.email, u.username, u.emp_id];
    for (const key of userIdentifiers) {
      if (key) {
        const str = String(key).trim();
        if (enrolledUserIds.has(str) || enrolledUserIds.has(str.toLowerCase())) {
          return true;
        }
      }
    }

    return false;
  }, [enrolledUserIds]);

  const filteredUsers = React.useMemo(() => {
    let result = sorted;
    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      result = result.filter(u => 
        (u.username || "").toLowerCase().includes(lower) || 
        (u.first_name || "").toLowerCase().includes(lower) ||
        (u.last_name || "").toLowerCase().includes(lower) ||
        (u.email || "").toLowerCase().includes(lower) ||
        (u.emp_id || "").toLowerCase().includes(lower)
      );
    }
    if (mfaFilter === 'Not Enrolled') {
      result = result.filter(u => !isUserMfaEnrolled(u));
    } else if (mfaFilter === 'Enrolled') {
      result = result.filter(u => isUserMfaEnrolled(u));
    }
    if (sourceFilter) {
      const sf = sourceFilter.toLowerCase();
      result = result.filter(u => {
        const s = (u.source || '').toLowerCase();
        if (sf === 'active directory' || sf === 'ad') return s === 'ad';
        if (sf === 'scim') return s === 'scim';
        if (sf === 'manual') return s === 'manual' || !s;
        return true;
      });
    }
    if (lockedFilter === 'Locked') {
      result = result.filter(u => isUserLocked(u));
    } else if (lockedFilter === 'Unlocked') {
      result = result.filter(u => !isUserLocked(u));
    }
    return result;
  }, [sorted, searchQuery, mfaFilter, sourceFilter, lockedFilter, isUserMfaEnrolled]);

  const formFields = [
    { name: 'first_name', label: 'First Name' },
    { name: 'last_name', label: 'Last Name' },
    { name: 'username', label: 'User Name' },
    {
      name: 'password',
      label: isEdit
        ? (formData.authMethod === 'ad' || formData.source === 'ad')
          ? 'Password'
          : 'Password (leave blank to keep unchanged)'
        : 'Password',
      type: isEdit && (formData.authMethod === 'ad' || formData.source === 'ad') ? 'text' : 'password'
    },
    {
      name: 'roleId',
      label: 'Role',
      select: true,
      options: roles,
      optionLabel: 'role'
    },
    { name: 'department', label: 'Department' },
    { name: 'mobile', label: 'Mobile Number' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'designation', label: 'Designation' },
    { name: 'emp_id', label: 'Employee ID' }
  ];

  if (!canRead) {
    return <AccessDenied featureName="User Management" title="User Management" />;
  }

  return (
    <Box p={2} style={V}>
      <ReusableHeader
        title="User Management"
        breadcrumbs={[{ label: "User Management" }]}
        actionButton={
          <Stack direction="row" spacing={1.5} alignItems="center">
            {canRead && (
              <Button
                size="small"
                variant="outlined"
                onClick={openSecurityDialog}
                className="global-button"
                sx={{
                  borderColor: 'var(--hairline)',
                  color: 'var(--vault-navy)',
                  fontWeight: 600,
                  fontSize: 13,
                  textTransform: 'none',
                  borderRadius: '6px',
                  '&:hover': {
                    borderColor: 'var(--edge-blue)',
                    backgroundColor: 'var(--blue-tint)',
                  },
                }}
              >
                Login security
              </Button>
            )}
            {canCreate && (
              <Button
                size="small"
                variant="contained"
                onClick={() => openDialog()}
                className="global-button global-button-primary"
              >
                Add User
              </Button>
            )}
          </Stack>
        }
      />

      <Box sx={{ mt: 3 }}>
        <TableFilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={[
            {
              id: 'source',
              label: 'Source',
              options: ['Manual', 'SCIM', 'Active Directory'],
              value: sourceFilter,
            },
            {
              id: 'mfa',
              label: 'MFA Status',
              options: ['Enrolled', 'Not Enrolled'],
              value: mfaFilter,
            },
            {
              id: 'locked',
              label: 'Locked',
              options: ['Locked', 'Unlocked'],
              value: lockedFilter,
            },
          ]}
          onFilterChange={(id, val) => {
            if (id === 'source') {
              setSourceFilter(val);
              setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                if (val) next.set('source', val);
                else next.delete('source');
                return next;
              }, { replace: true });
            } else if (id === 'mfa') {
              setMfaFilter(val);
              setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                if (val === 'Not Enrolled') next.set('mfa', 'disabled');
                else if (val === 'Enrolled') next.set('mfa', 'enabled');
                else next.delete('mfa');
                return next;
              }, { replace: true });
            } else if (id === 'locked') {
              setLockedFilter(val);
              setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                if (val === 'Locked') next.set('locked', 'true');
                else if (val === 'Unlocked') next.set('locked', 'false');
                else next.delete('locked');
                return next;
              }, { replace: true });
            }
          }}
          searchPlaceholder="Search Users..."
        />

      <ReusableTable
        columns={[
          { id: "index", label: "#", render: (row, idx) => idx + 1, width: 55 },
          { 
            id: "username", 
            label: "Username", 
            width: 180,
            render: (row) => (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box sx={{ width: 32, height: 32, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--blue-tint)', color: 'var(--edge-blue)' }}>
                  <PersonIcon fontSize="small" />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--vault-navy)' }}>
                  {row.username || "—"}
                </Typography>
              </Stack>
            )
          },
          { id: "emp_id", label: "Employee ID", render: (row) => row.emp_id ? <Typography variant="body2" sx={{ color: 'var(--edge-blue)', fontWeight: 600 }}>{row.emp_id}</Typography> : '—', width: 125 },
          { id: "first_name", label: "First", width: 105 },
          { id: "last_name", label: "Last", width: 120 },
          { 
            id: "email", 
            label: "Email", 
            width: 250,
            render: (row) => (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'var(--slate)' }}>
                <EmailIcon fontSize="small" />
                <Typography variant="body2">{row.email || '—'}</Typography>
              </Stack>
            )
          },
          { id: "mobile", label: "Mobile", render: (row) => row.mobile || '—', width: 140 },
          { 
            id: "role", 
            label: "Role", 
            render: (row) => {
              const roleName = roles.find(r => r._id === row.roleId)?.role;
              if (!roleName) return '—';
              return (
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'var(--vault-navy)' }}>
                  <AdminPanelSettingsIcon fontSize="small" sx={{ color: 'var(--edge-blue)' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{roleName}</Typography>
                </Stack>
              );
            }, 
            width: 160 
          },
          { id: "department", label: "Department", render: (row) => row.department || '—', width: 160 },
          {
            id: "source",
            label: "Source",
            width: 170,
            render: (row) => {
              const src = (row.source || '').toLowerCase();
              let label = 'Manual';
              let chipSx = {
                color: 'var(--slate)',
                backgroundColor: 'var(--mist)',
                border: '1px solid var(--hairline)',
              };
              if (src === 'ad') {
                label = 'Active Directory';
                chipSx = {
                  color: 'var(--edge-blue)',
                  backgroundColor: 'var(--blue-tint)',
                  border: '1px solid rgba(31, 111, 235, 0.3)',
                };
              } else if (src === 'scim') {
                label = 'SCIM';
                chipSx = {
                  color: 'var(--secure)',
                  backgroundColor: 'rgba(0, 168, 143, 0.12)',
                  border: '1px solid rgba(0, 168, 143, 0.3)',
                };
              }
              return (
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    label={label}
                    sx={{
                      fontWeight: 600,
                      fontSize: 11,
                      height: 22,
                      ...chipSx,
                    }}
                  />
                  {row.mustChangePassword && (
                    <Chip
                      size="small"
                      label="Password change required"
                      sx={{
                        fontWeight: 600,
                        fontSize: 11,
                        backgroundColor: 'rgba(240, 162, 2, 0.12)',
                        color: 'var(--elevated)',
                        border: '1px solid rgba(240, 162, 2, 0.3)',
                        height: 22,
                      }}
                    />
                  )}
                  {isUserLocked(row) && (
                    <Tooltip title={getLockTooltip(row)} arrow placement="top">
                      <Chip
                        size="small"
                        label="Locked"
                        sx={{
                          fontWeight: 600,
                          fontSize: 11,
                          backgroundColor: 'rgba(214, 50, 58, 0.12)',
                          color: 'var(--critical)',
                          border: '1px solid rgba(214, 50, 58, 0.3)',
                          height: 22,
                        }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              );
            },
          },
          { 
            id: "mfaStatus", 
            label: "MFA Status", 
            render: (row) => {
              const isEnrolled = isUserMfaEnrolled(row);
              return (
                <Chip
                  size="small"
                  label={isEnrolled ? "Enrolled" : "Not Enrolled"}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    backgroundColor: isEnrolled ? "rgba(0, 168, 143, 0.12)" : "rgba(240, 162, 2, 0.12)",
                    color: isEnrolled ? "var(--secure)" : "var(--elevated)",
                    border: `1px solid ${isEnrolled ? "rgba(0, 168, 143, 0.3)" : "rgba(240, 162, 2, 0.3)"}`,
                    height: 22,
                  }}
                />
              );
            },
            width: 130 
          },
          {
            id: "actions",
            label: "Actions",
            align: "right",
            width: 120,
            render: (row) => (
              <Box className="table-actions" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                {canUpdate && isUserLocked(row) && (
                  <IconButton
                    size="small"
                    onClick={() => handleUnlockClick(row)}
                    className="global-icon-button global-icon-button-edit"
                    title="Unlock User"
                    aria-label="Unlock User"
                  >
                    <LockOpenIcon fontSize="small" />
                  </IconButton>
                )}
                {canUpdate && (
                  <IconButton size="small" onClick={() => openDialog(row)} className="global-icon-button global-icon-button-edit" title="Edit User">
                    <ModeEditIcon fontSize="small" />
                  </IconButton>
                )}
                {canDelete && (
                  <IconButton size="small" onClick={() => handleDeleteClick(row)} className="global-icon-button global-icon-button-delete" title="Delete User">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )
          }
        ]}
        data={filteredUsers}
        loading={loading}
        emptyMessage="No users found."
      />
      </Box>

      {/* Add / Edit dialog */}
      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{
          className: 'global-card',
          style: V,
          sx: {
            borderRadius: '8px',
            overflow: 'hidden',
            maxHeight: 'calc(100vh - 48px)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid var(--hairline)',
            flexShrink: 0,
            backgroundColor: 'var(--canvas)',
          }}
        >
          <Typography variant="h6" sx={{ color: 'var(--vault-navy)', fontWeight: 700, fontSize: '1.1rem' }}>
            {isEdit ? 'Edit User' : 'Add User'}
          </Typography>
          <IconButton onClick={closeDialog} sx={{ color: 'var(--slate)', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <DialogContent
            sx={{
              p: 3,
              backgroundColor: 'var(--canvas)',
              overflowY: 'auto',
              flex: 1,
              minHeight: 0,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--hairline)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'var(--slate)',
              },
            }}
          >
            <Grid container spacing={2.5}>
              {formFields.map((field, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75 }}>
                      {field.label}
                    </Typography>
                    {field.select ? (
                      <Select
                        fullWidth
                        size="small"
                        name={field.name}
                        value={formData[field.name] || ''}
                        onChange={handleChange}
                        MenuProps={selectMenuProps}
                        displayEmpty
                        sx={{ borderRadius: '6px' }}
                      >
                        <MenuItem value="" disabled>
                          <span style={{ color: 'var(--slate)' }}>Select {field.label}</span>
                        </MenuItem>
                        {field.options.map(opt => (
                          <MenuItem key={opt._id} value={opt._id}>
                            {opt[field.optionLabel]}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        name={field.name}
                        type={
                          field.name === 'password' && isEdit && (formData.authMethod === 'ad' || formData.source === 'ad')
                            ? 'text'
                            : field.type || 'text'
                        }
                        value={
                          field.name === 'password' && isEdit && (formData.authMethod === 'ad' || formData.source === 'ad')
                            ? 'Password is managed by Active Directory'
                            : formData[field.name] || ''
                        }
                        onChange={handleChange}
                        disabled={
                          field.name === 'password' && isEdit && (formData.authMethod === 'ad' || formData.source === 'ad')
                        }
                        helperText={
                          field.name === 'password' && isEdit && (formData.authMethod === 'ad' || formData.source === 'ad')
                            ? 'Password is managed by Active Directory'
                            : undefined
                        }
                        placeholder={
                          field.name === 'password' && isEdit
                            ? (formData.authMethod === 'ad' || formData.source === 'ad')
                              ? 'Password is managed by Active Directory'
                              : 'Leave blank to keep unchanged'
                            : `Enter ${field.label.toLowerCase()}`
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '6px' },
                          '& .MuiInputBase-input:-webkit-autofill': {
                            WebkitBoxShadow: '0 0 0 1000px var(--canvas) inset !important',
                            boxShadow: '0 0 0 1000px var(--canvas) inset !important',
                            WebkitTextFillColor: 'var(--vault-navy) !important',
                            transition: 'background-color 5000s ease-in-out 0s !important',
                          },
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              ))}

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75 }}>
                    Status
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    name="status"
                    value={formData.status || 'active'}
                    onChange={handleChange}
                    MenuProps={selectMenuProps}
                    sx={{ borderRadius: '6px' }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                    <MenuItem value="deleted">Deleted</MenuItem>
                  </Select>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75 }}>
                  Profile Image (optional)
                </Typography>
                <input
                  accept="image/*"
                  id="profile-image"
                  type="file"
                  onChange={handleFileChange}
                  className="global-file-input"
                />
                {formData.profile_image_file ? (
                  <img
                    alt="preview"
                    src={URL.createObjectURL(formData.profile_image_file)}
                    className="global-image-preview"
                  />
                ) : isEdit && formData.profile_image ? (
                  <img
                    alt="current"
                    src={formData.profile_image}
                    className="global-image-preview"
                  />
                ) : null}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: '1px solid var(--hairline)',
              backgroundColor: 'var(--mist)',
              flexShrink: 0,
            }}
          >
            <Button
              onClick={closeDialog}
              className="global-button global-button-text"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              className="global-button global-button-primary"
            >
              {loading ? (isEdit ? 'Updating…' : 'Saving…') : (isEdit ? 'Update' : 'Save')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteOpen}
        onClose={handleCancelDelete}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: 'global-card', style: V, sx: { borderRadius: '8px', overflow: 'hidden' } }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, py: 2, borderBottom: '1px solid var(--hairline)' }}
        >
          <Typography variant="h6" sx={{ color: 'var(--vault-navy)', fontWeight: 700, fontSize: '1.1rem' }}>
            Delete User
          </Typography>
          <IconButton onClick={handleCancelDelete} sx={{ color: 'var(--slate)', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography sx={{ color: 'var(--vault-navy)', fontSize: '0.95rem' }}>
            Are you sure you want to delete the user{' '}
            <strong style={{ color: 'var(--vault-navy)' }}>
              {deletingUser ? (deletingUser.username || deletingUser.email) : ''}
            </strong>?
          </Typography>
          <Typography sx={{ mt: 1, color: 'var(--slate)', fontSize: '0.85rem' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid var(--hairline)', backgroundColor: 'var(--mist)' }}>
          <Button
            onClick={handleCancelDelete}
            disabled={deleting}
            className="global-button global-button-text"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            className="global-button-danger"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unlock confirmation dialog */}
      <Dialog
        open={unlockOpen}
        onClose={handleCancelUnlock}
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: 'global-card', style: V, sx: { borderRadius: '8px', overflow: 'hidden' } }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, py: 2, borderBottom: '1px solid var(--hairline)' }}
        >
          <Typography variant="h6" sx={{ color: 'var(--vault-navy)', fontWeight: 700, fontSize: '1.1rem' }}>
            Unlock User
          </Typography>
          <IconButton onClick={handleCancelUnlock} sx={{ color: 'var(--slate)', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography sx={{ color: 'var(--vault-navy)', fontSize: '0.95rem' }}>
            Are you sure you want to unlock user{' '}
            <strong style={{ color: 'var(--vault-navy)' }}>
              {unlockingUser ? (unlockingUser.username || unlockingUser.email) : ''}
            </strong>?
          </Typography>
          <Typography sx={{ mt: 1, color: 'var(--slate)', fontSize: '0.85rem' }}>
            This will reset failed login attempts and allow the user to sign in immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid var(--hairline)', backgroundColor: 'var(--mist)' }}>
          <Button
            onClick={handleCancelUnlock}
            disabled={unlocking}
            className="global-button global-button-text"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmUnlock}
            disabled={unlocking}
            className="global-button global-button-primary"
          >
            {unlocking ? 'Unlocking…' : 'Unlock'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Security dialog */}
      <Dialog
        open={securityOpen}
        onClose={() => setSecurityOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          className: 'global-card',
          style: V,
          sx: { borderRadius: '8px', overflow: 'hidden' },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, py: 2, borderBottom: '1px solid var(--hairline)' }}
        >
          <Typography variant="h6" sx={{ color: 'var(--vault-navy)', fontWeight: 700, fontSize: '1.1rem' }}>
            Login Security Settings
          </Typography>
          <IconButton onClick={() => setSecurityOpen(false)} sx={{ color: 'var(--slate)', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Box component="form" onSubmit={handleSaveSecurityPolicy}>
          <DialogContent sx={{ p: 3, backgroundColor: 'var(--canvas)' }}>
            {securityServerError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {securityServerError}
              </Alert>
            )}
            {!canUpdate && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You have read-only access to login security settings.
              </Alert>
            )}
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75 }}>
                  Maximum failed attempts
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="maxFailedAttempts"
                  type="number"
                  disabled={!canUpdate || securitySaving}
                  value={policyForm.maxFailedAttempts}
                  onChange={handlePolicyChange}
                  error={Boolean(policyErrors.maxFailedAttempts)}
                  helperText={policyErrors.maxFailedAttempts || 'Number of failed login attempts before lockout (3–10)'}
                  inputProps={{ min: 3, max: 10, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75 }}>
                  Lock duration
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="lockDurationMinutes"
                  type="number"
                  disabled={!canUpdate || securitySaving}
                  value={policyForm.lockDurationMinutes}
                  onChange={handlePolicyChange}
                  error={Boolean(policyErrors.lockDurationMinutes)}
                  helperText={policyErrors.lockDurationMinutes || '0 = until an administrator unlocks, otherwise 5–1440 minutes'}
                  inputProps={{ min: 0, max: 1440, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--vault-navy)', mb: 0.75 }}>
                  Failed-attempt window
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  name="observationWindowMinutes"
                  type="number"
                  disabled={!canUpdate || securitySaving}
                  value={policyForm.observationWindowMinutes}
                  onChange={handlePolicyChange}
                  error={Boolean(policyErrors.observationWindowMinutes)}
                  helperText={policyErrors.observationWindowMinutes || 'Time window for tracking failed attempts in minutes (5–1440)'}
                  inputProps={{ min: 5, max: 1440, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              borderTop: '1px solid var(--hairline)',
              backgroundColor: 'var(--mist)',
            }}
          >
            <Button
              onClick={() => setSecurityOpen(false)}
              className="global-button global-button-text"
            >
              Cancel
            </Button>
            {canUpdate && (
              <Button
                type="submit"
                variant="contained"
                disabled={isSaveDisabled}
                className="global-button global-button-primary"
              >
                {securitySaving ? 'Saving…' : 'Save'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      {/* Snackbar — also portaled, so it gets its own var scope */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackClose}
          severity={snackSeverity}
          className="global-alert"
          style={V}
          sx={{
            width: '100%',
            backgroundColor: 'var(--canvas)',
            color: 'var(--vault-navy)',
            border: '1px solid var(--hairline)',
          }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};